import { FPPError } from "./FPPError";
import { v4 as uuid } from "uuid";

type ErrorResponseBody = {
    message: string;
    errorCode: string;
    errorTraceId: string;
};

export const isErrObj = (error: unknown): error is Error => {
    return error instanceof Error;
};

export const isFPPErrObj = (error: unknown): error is FPPError => {
    return error instanceof FPPError;
};

/**
 * Builds error code
 *
 * @param mfShorty shorty of the micro frontend the error occured in
 * @param errorSerial error serial of the error in the micro frontend
 * @returns final error code built using the given parameters
 */
export const buildErrorCode = (mfShorty: string, errorSerial: string): string => {
    return `${mfShorty}_${errorSerial}`;
};

/**
 * Simplifies error code by cutting off error trace id
 *
 * @param errorCode error code as returned from the backend
 * @returns error code consisting of only 2 parts (mfShorty and errorSerial)
 */
export const simplifyErrorCode = (errorCode: string): string => {
    const errCodeParts = errorCode.split("_");
    if (errCodeParts.length > 2) {
        errCodeParts.pop();
        return errCodeParts.join("_");
    }
    return errorCode;
};

export const buildErrorTraceId = (): string => {
    return uuid();
};

/**
 * Checks if error originated in current mf by checking the errorCode
 *
 * @param mfShorty shorty of the current micro frontend
 * @param error to check
 * @returns true if error originated in current mf, false otherwise
 */
export const errorOriginatedInMf = (mfShorty: string, error: FPPError): boolean => {
    return error.errorCode.startsWith(mfShorty);
};

/**
 * Builds a standardized error message for fetch requests to be used for logging
 * (e.g. as message in sendErrorToBackend)
 *
 * @param httpMethod method used in the fetch request
 * @param response response of the fetch request
 * @returns a standardized error message for a fetch request error based on the given data
 */
export const buildFetchErrorMessage = (httpMethod: string, response: Response, message?: string): string => {
    const defaultErrMsg = "An unknown Error occurred.";
    return `${httpMethod} ${response.url} ${response.status} (${response.statusText}): ${message ?? defaultErrMsg}`;
};

/**
 * Builds a standardized error object of type FPPError
 * for regular ts/js fetch requests
 *
 * @param mfShorty shorty for the micro frontend the error occured in
 * @param httpMethod method used in the fetch request
 * @param response response of the fetch request
 * @param errorSerial error serial of the error in the micro frontend
 * @returns a standardized error object for a fetch request error based on the given data
 */
export const buildFetchErrorObj = async (
    mfShorty: string,
    httpMethod: string,
    response: Response,
    errorSerial: string,
): Promise<FPPError> => {
    const responseBody: ErrorResponseBody | undefined = await response.json().catch(() => undefined);

    const error = new FPPError(
        buildFetchErrorMessage(httpMethod, response, responseBody?.message),
        responseBody?.errorCode ? simplifyErrorCode(responseBody.errorCode) : buildErrorCode(mfShorty, errorSerial),
        responseBody?.errorTraceId ?? buildErrorTraceId(),
    );
    error.httpStatusCode = response.status;

    return error;
};

/**
 * Builds a standardized error object of type FPPError
 * for fetch requests via "openapi-fetch" client
 *
 * @param mfShorty shorty for the micro frontend the error occured in
 * @param errorSerial error serial of the error in the micro frontend
 * @param httpMethod method used in the fetch request
 * @param response response of the fetch request
 * @param errorResponseBody error response body of the fetch request
 * @returns a standardized error object for a fetch request error based on the given data
 */
export const buildApiClientFetchErrorObj = async (
    mfShorty: string,
    errorSerial: string,
    httpMethod: string,
    response: Response,
    errorResponseBody?: ErrorResponseBody,
): Promise<FPPError> => {
    const error = new FPPError(
        buildFetchErrorMessage(httpMethod, response, errorResponseBody?.message),
        errorResponseBody?.errorCode ? simplifyErrorCode(errorResponseBody.errorCode) : buildErrorCode(mfShorty, errorSerial),
        errorResponseBody?.errorTraceId ?? buildErrorTraceId(),
    );
    error.httpStatusCode = response.status;

    return error;
};

/**
 * Builds a standardized error object of type FPPError from an unknown error object
 *
 * @param mfShorty shorty for the micro frontend the error occurred in
 * @param error unknown error object to transform to FPPError, can be `null` or `undefined`
 * @param errorSerial error serial of the error in the micro frontend
 * @param message description of the error. If not given, `error.message` or a default message is used.
 * @returns FPPError
 */
export const buildErrorObj = (mfShorty: string, error: unknown, errorSerial: string, message?: string): FPPError => {
    const defaultErrMsg = "An unknown Error occurred.";

    if (!error) {
        return new FPPError(message ?? defaultErrMsg, buildErrorCode(mfShorty, errorSerial), buildErrorTraceId());
    }

    if (isFPPErrObj(error)) {
        return error;
    }

    if (isErrObj(error)) {
        let errorMessage = error.message;
        errorMessage = errorMessage === "" ? defaultErrMsg : errorMessage;

        return new FPPError(message ?? errorMessage, buildErrorCode(mfShorty, errorSerial), buildErrorTraceId(), error.stack);
    }

    let errorStr = error + "";
    errorStr = errorStr === "" ? defaultErrMsg : errorStr;

    return new FPPError(message ?? errorStr, buildErrorCode(mfShorty, errorSerial), buildErrorTraceId());
};
